import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  ExpandedState,
  getExpandedRowModel,
} from '@tanstack/react-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  Search,
  Table2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
} from 'lucide-react';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface ServiceDetail {
  name: string;
  count: number;
  revenue: number;
  percentage: number;
}

interface StaffPerformance {
  name: string;
  customerCount: number;
  revenue: number;
  efficiency: number;
}

interface SalesRow {
  id: string;
  date: string;
  revenue: number;
  customers: number;
  averageSpend: number;
  newCustomers: number;
  returningCustomers: number;
  newCustomerRate: number;
  serviceDetails: ServiceDetail[];
  staffDetails: StaffPerformance[];
}

interface DetailedBreakdownProps {
  startDate?: Date;
  endDate?: Date;
  onExport?: (data: SalesRow[]) => void;
}

// Framer Motion バリアント定義
const dialogVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: -20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.2,
    },
  },
};

const customPeriodVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.2,
    },
  },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3,
      ease: [0.4, 0.0, 0.2, 1], // Apple風イージング
    },
  },
};

const alertVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
};

const buttonHoverVariants = {
  rest: { scale: 1 },
  hover: {
    scale: 1.02,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 10,
    },
  },
  tap: { scale: 0.98 },
};

const radioItemVariants = {
  rest: { scale: 1, x: 0 },
  hover: {
    scale: 1.01,
    x: 4,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 20,
    },
  },
};

export const DetailedBreakdown: React.FC<DetailedBreakdownProps> = ({
  onExport
}) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'date', desc: true }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [selectedFilters, setSelectedFilters] = useState<{
    minRevenue?: number;
    minCustomers?: number;
    startDate?: Date;
    endDate?: Date;
    minNewCustomerRate?: number;
    maxNewCustomerRate?: number;
    staffNames?: string[];
  }>({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [staffCommandOpen, setStaffCommandOpen] = useState(false);
  const [staffSearchInput, setStaffSearchInput] = useState('');

  // CSV Export Dialog状態管理
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState<'basic' | 'detailed' | 'full' | 'menu-breakdown' | 'staff-breakdown'>('basic');
  const [dateRangeType, setDateRangeType] = useState<'all' | 'thisMonth' | 'lastMonth' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();
  const [isExporting, setIsExporting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);

  // モックデータ生成
  const generateMockData = (): SalesRow[] => {
    const services = ['ヘッドスパ', 'カット', 'カラー', 'パーマ', 'トリートメント'];
    const staff = ['山田太郎', '佐藤花子', '鈴木一郎', '田中美咲', '渡辺健'];

    return Array.from({ length: 30 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - index);
      const revenue = Math.floor(Math.random() * 150000) + 50000;
      const customers = Math.floor(Math.random() * 40) + 10;
      const newCustomers = Math.floor(customers * (0.2 + Math.random() * 0.3));
      const returningCustomers = customers - newCustomers;

      return {
        id: `row-${index}`,
        date: date.toISOString().split('T')[0],
        revenue,
        customers,
        averageSpend: Math.floor(revenue / customers),
        newCustomers,
        returningCustomers,
        newCustomerRate: Math.round((newCustomers / customers) * 100),
        serviceDetails: services.slice(0, 3).map(service => ({
          name: service,
          count: Math.floor(Math.random() * 15) + 1,
          revenue: Math.floor(Math.random() * 30000) + 10000,
          percentage: Math.random() * 30 + 10,
        })),
        staffDetails: staff.slice(0, 3).map(name => ({
          name,
          customerCount: Math.floor(Math.random() * 10) + 1,
          revenue: Math.floor(Math.random() * 25000) + 5000,
          efficiency: Math.random() * 100,
        })),
      };
    });
  };

  const rows = useMemo(() => generateMockData(), []);

  // 利用可能なスタッフ名リストを抽出
  const availableStaffNames = useMemo(() => {
    const uniqueNames = new Set<string>();
    rows.forEach(row => {
      row.staffDetails.forEach(staff => {
        uniqueNames.add(staff.name);
      });
    });
    return Array.from(uniqueNames).sort();
  }, [rows]);

  // スタッフ名検索フィルタリング
  const filteredStaffNames = useMemo(() => {
    if (!staffSearchInput) return availableStaffNames;
    return availableStaffNames.filter(name =>
      name.toLowerCase().includes(staffSearchInput.toLowerCase())
    );
  }, [availableStaffNames, staffSearchInput]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(value);
  };

  // CSV Injection対策関数
  const escapeCSVInjection = (value: string): string => {
    const dangerousChars = ['=', '+', '-', '@', '\t', '\r'];
    if (dangerousChars.some(char => value.startsWith(char))) {
      return `'${value}`;
    }
    return value;
  };

  // テーブルカラム定義
  const columns: ColumnDef<SalesRow>[] = [
    {
      id: 'expand',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => row.toggleExpanded()}
          aria-label={row.getIsExpanded() ? '詳細を閉じる' : '詳細を開く'}
          aria-expanded={row.getIsExpanded()}
          className="h-8 w-8"
        >
          {row.getIsExpanded() ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      ),
    },
    {
      accessorKey: 'date',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 font-semibold"
          aria-label="日付でソート"
        >
          日付
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-sm font-medium">
          {new Date(row.getValue('date')).toLocaleDateString('ja-JP', {
            month: 'short',
            day: 'numeric',
            weekday: 'short',
          })}
        </span>
      ),
    },
    {
      accessorKey: 'revenue',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 font-semibold"
          aria-label="売上でソート"
        >
          売上
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-sm font-bold">{formatCurrency(row.getValue('revenue'))}</span>
      ),
    },
    {
      accessorKey: 'customers',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 font-semibold"
          aria-label="客数でソート"
        >
          客数
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span className="text-sm">{row.getValue('customers')}名</span>,
    },
    {
      accessorKey: 'averageSpend',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 font-semibold"
          aria-label="平均単価でソート"
        >
          平均単価
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span className="text-sm">{formatCurrency(row.getValue('averageSpend'))}</span>,
    },
    {
      accessorKey: 'newCustomers',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 font-semibold hidden md:flex"
          aria-label="新規顧客数でソート"
        >
          新規顧客数
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-sm font-medium hidden md:table-cell">{row.getValue('newCustomers')}名</span>
      ),
    },
    {
      accessorKey: 'returningCustomers',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 font-semibold hidden md:flex"
          aria-label="リピート顧客数でソート"
        >
          リピート顧客数
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-sm font-medium hidden md:table-cell">{row.getValue('returningCustomers')}名</span>
      ),
    },
    {
      accessorKey: 'newCustomerRate',
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="h-8 px-2 font-semibold hidden md:flex"
          aria-label="新規顧客率でソート"
        >
          新規顧客率
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <span className="text-sm font-medium text-primary hidden md:table-cell">
          {row.getValue('newCustomerRate')}%
        </span>
      ),
    },
  ];

  // フィルタリングロジック
  const filteredData = useMemo(() => {
    let filtered = rows;

    // 最小売上フィルター
    if (selectedFilters.minRevenue) {
      filtered = filtered.filter(row => row.revenue >= selectedFilters.minRevenue!);
    }

    // 最小客数フィルター
    if (selectedFilters.minCustomers) {
      filtered = filtered.filter(row => row.customers >= selectedFilters.minCustomers!);
    }

    // 期間フィルター
    if (selectedFilters.startDate) {
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.date);
        return rowDate >= selectedFilters.startDate!;
      });
    }
    if (selectedFilters.endDate) {
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.date);
        return rowDate <= selectedFilters.endDate!;
      });
    }

    // 新規顧客率フィルター
    if (selectedFilters.minNewCustomerRate !== undefined) {
      filtered = filtered.filter(row => row.newCustomerRate >= selectedFilters.minNewCustomerRate!);
    }
    if (selectedFilters.maxNewCustomerRate !== undefined) {
      filtered = filtered.filter(row => row.newCustomerRate <= selectedFilters.maxNewCustomerRate!);
    }

    // スタッフフィルター
    if (selectedFilters.staffNames && selectedFilters.staffNames.length > 0) {
      filtered = filtered.filter(row => {
        return row.staffDetails.some(staff =>
          selectedFilters.staffNames!.some(filterName =>
            staff.name.includes(filterName)
          )
        );
      });
    }

    return filtered;
  }, [rows, selectedFilters]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    state: {
      sorting,
      globalFilter,
      expanded,
      pagination,
    },
  });

  const getDateRangeFilteredRows = (): SalesRow[] => {
    const allRows = table.getFilteredRowModel().rows.map(row => row.original);

    if (dateRangeType === 'all') {
      return allRows;
    }

    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (dateRangeType) {
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'custom':
        if (!customStartDate || !customEndDate) {
          return allRows;
        }
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        return allRows;
    }

    return allRows.filter((row) => {
      const rowDate = new Date(row.date);
      return rowDate >= startDate && rowDate <= endDate;
    });
  };

  const generateCSVContent = (): string => {
    const filteredRows = getDateRangeFilteredRows();

    if (exportType === 'basic') {
      const headers = ['日付', '売上', '客数', '平均単価', '新規顧客数', 'リピート顧客数', '新規顧客率'];
      const rows = filteredRows.map((row) => [
        escapeCSVInjection(row.date),
        row.revenue.toString(),
        row.customers.toString(),
        row.averageSpend.toString(),
        row.newCustomers.toString(),
        row.returningCustomers.toString(),
        row.newCustomerRate + '%',
      ]);
      return [headers, ...rows].map((row) => row.join(',')).join('\n');
    }

    if (exportType === 'detailed') {
      const headers = [
        '日付',
        '売上',
        '客数',
        '平均単価',
        '新規顧客数',
        'リピート顧客数',
        '新規顧客率',
        'メニュー1位名',
        'メニュー1位売上',
        'メニュー2位名',
        'メニュー2位売上',
        'メニュー3位名',
        'メニュー3位売上',
        'スタッフ1位名',
        'スタッフ1位売上',
        'スタッフ2位名',
        'スタッフ2位売上',
        'スタッフ3位名',
        'スタッフ3位売上',
      ];
      const rows = filteredRows.map((row) => {
        const topServices = row.serviceDetails.slice(0, 3);
        const topStaff = row.staffDetails.slice(0, 3);

        return [
          escapeCSVInjection(row.date),
          row.revenue.toString(),
          row.customers.toString(),
          row.averageSpend.toString(),
          row.newCustomers.toString(),
          row.returningCustomers.toString(),
          row.newCustomerRate + '%',
          // メニューTOP3（個別カラム）
          topServices[0] ? escapeCSVInjection(topServices[0].name) : '',
          topServices[0] ? topServices[0].revenue.toString() : '',
          topServices[1] ? escapeCSVInjection(topServices[1].name) : '',
          topServices[1] ? topServices[1].revenue.toString() : '',
          topServices[2] ? escapeCSVInjection(topServices[2].name) : '',
          topServices[2] ? topServices[2].revenue.toString() : '',
          // スタッフTOP3（個別カラム）
          topStaff[0] ? escapeCSVInjection(topStaff[0].name) : '',
          topStaff[0] ? topStaff[0].revenue.toString() : '',
          topStaff[1] ? escapeCSVInjection(topStaff[1].name) : '',
          topStaff[1] ? topStaff[1].revenue.toString() : '',
          topStaff[2] ? escapeCSVInjection(topStaff[2].name) : '',
          topStaff[2] ? topStaff[2].revenue.toString() : '',
        ];
      });
      return [headers, ...rows].map((row) => row.join(',')).join('\n');
    }

    // メニュー別売上（メニュー名・日付・売上・販売数）
    if (exportType === 'menu-breakdown') {
      const headers = ['メニュー名', '日付', '売上', '販売数'];
      const rows: string[][] = [];

      filteredRows.forEach((row) => {
        row.serviceDetails.forEach((service) => {
          rows.push([
            escapeCSVInjection(service.name),
            escapeCSVInjection(row.date),
            service.revenue.toString(),
            service.count.toString(),
          ]);
        });
      });

      return [headers, ...rows].map((row) => row.join(',')).join('\n');
    }

    // スタッフ別売上（スタッフ名・日付・売上・客数）
    if (exportType === 'staff-breakdown') {
      const headers = ['スタッフ名', '日付', '売上', '客数'];
      const rows: string[][] = [];

      filteredRows.forEach((row) => {
        row.staffDetails.forEach((staff) => {
          rows.push([
            escapeCSVInjection(staff.name),
            escapeCSVInjection(row.date),
            staff.revenue.toString(),
            staff.customerCount.toString(),
          ]);
        });
      });

      return [headers, ...rows].map((row) => row.join(',')).join('\n');
    }

    // 全データ（メニュー・スタッフ別に行展開）※非推奨
    const headers = [
      '日付',
      '売上',
      '客数',
      '平均単価',
      '新規顧客数',
      'リピート顧客数',
      '新規顧客率',
      'メニュー名',
      'メニュー売上',
      'スタッフ名',
      'スタッフ売上',
      '内訳種別',
    ];
    const rows: string[][] = [];

    filteredRows.forEach((row) => {
      row.serviceDetails.forEach((service) => {
        rows.push([
          escapeCSVInjection(row.date),
          row.revenue.toString(),
          row.customers.toString(),
          row.averageSpend.toString(),
          row.newCustomers.toString(),
          row.returningCustomers.toString(),
          row.newCustomerRate + '%',
          escapeCSVInjection(service.name),
          service.revenue.toString(),
          '',
          '',
          'メニュー',
        ]);
      });

      row.staffDetails.forEach((staff) => {
        rows.push([
          escapeCSVInjection(row.date),
          row.revenue.toString(),
          row.customers.toString(),
          row.averageSpend.toString(),
          row.newCustomers.toString(),
          row.returningCustomers.toString(),
          row.newCustomerRate + '%',
          '',
          '',
          escapeCSVInjection(staff.name),
          staff.revenue.toString(),
          'スタッフ',
        ]);
      });
    });

    return [headers, ...rows].map((row) => row.join(',')).join('\n');
  };

  const validateDates = (): boolean => {
    setValidationError('');

    if (dateRangeType === 'custom') {
      if (!customStartDate || !customEndDate) {
        setValidationError('カスタム期間を選択する場合は、開始日と終了日を両方入力してください。');
        return false;
      }
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start > end) {
        setValidationError('開始日は終了日より前の日付を指定してください。');
        return false;
      }
      const daysDiff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff > 365) {
        setValidationError('期間は1年以内で指定してください。');
        return false;
      }
    }
    return true;
  };

  const handleExportCSV = async () => {
    if (!validateDates()) {
      return;
    }

    setIsExporting(true);
    setValidationError('');
    setExportSuccess(false);

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const csvContent = generateCSVContent();
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

      const filename = `売上データ_${exportType}_${dateRangeType}_${new Date().toISOString().split('T')[0]}.csv`;
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.setAttribute('aria-label', `${filename}をダウンロード`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportSuccess(true);

      setTimeout(() => {
        setExportDialogOpen(false);
        setExportSuccess(false);
      }, 2000);

      if (onExport) {
        onExport(getDateRangeFilteredRows());
      }
    } catch (error) {
      setValidationError('CSVファイルの生成中にエラーが発生しました。もう一度お試しください。');
      console.error('CSV Export Error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Table2 className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg font-semibold">詳細売上データ</CardTitle>
          </div>
          <div className="flex gap-2">
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="CSVエクスポートダイアログを開く"
                  title="CSVエクスポート"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent
                className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                aria-describedby="export-dialog-description"
              >
                <motion.div
                  variants={dialogVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <DialogHeader>
                    <DialogTitle id="export-dialog-title">売上データCSV出力</DialogTitle>
                    <DialogDescription id="export-dialog-description">
                      出力形式と期間を選択してCSVファイルをダウンロードします。
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-6 py-4">
                    {/* エクスポート形式選択 */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">出力形式</Label>
                      <RadioGroup
                        value={exportType}
                        onValueChange={(value) => {
                          setExportType(value as 'basic' | 'detailed' | 'full' | 'menu-breakdown' | 'staff-breakdown');
                          setValidationError('');
                        }}
                        aria-label="CSV出力形式選択"
                      >
                        {[
                          { value: 'basic', label: '基本データ（日付・売上・客数・平均単価・新規顧客・リピート顧客）' },
                          { value: 'detailed', label: '詳細データ（基本 + メニューTOP3 + スタッフTOP3）' },
                          { value: 'menu-breakdown', label: 'メニュー別売上（メニュー名・日付・売上・販売数）' },
                          { value: 'staff-breakdown', label: 'スタッフ別売上（スタッフ名・日付・売上・客数）' },
                          { value: 'full', label: '全データ（メニュー・スタッフ別に行展開）※非推奨' },
                        ].map((option) => (
                          <motion.div
                            key={option.value}
                            variants={radioItemVariants}
                            initial="rest"
                            whileHover="hover"
                            className="flex items-center space-x-2"
                          >
                            <RadioGroupItem value={option.value} id={`export-${option.value}`} />
                            <Label htmlFor={`export-${option.value}`} className="font-normal cursor-pointer">
                              {option.label}
                            </Label>
                          </motion.div>
                        ))}
                      </RadioGroup>
                    </div>

                    {/* 期間選択 */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">期間</Label>
                      <Select
                        value={dateRangeType}
                        onValueChange={(value) => {
                          setDateRangeType(value as 'all' | 'thisMonth' | 'lastMonth' | 'custom');
                          setValidationError('');
                        }}
                      >
                        <SelectTrigger aria-label="期間選択">
                          <SelectValue placeholder="期間を選択" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">全期間</SelectItem>
                          <SelectItem value="thisMonth">今月</SelectItem>
                          <SelectItem value="lastMonth">先月</SelectItem>
                          <SelectItem value="custom">カスタム期間</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* カスタム期間入力 - AnimatePresence */}
                    <AnimatePresence>
                      {dateRangeType === 'custom' && (
                        <motion.div
                          variants={customPeriodVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className="space-y-3 overflow-hidden"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="start-date">開始日</Label>
                              <DatePicker
                                id="start-date"
                                date={customStartDate}
                                onDateChange={(date) => {
                                  setCustomStartDate(date);
                                  setValidationError('');
                                }}
                                placeholder="開始日を選択"
                                aria-label="開始日"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="end-date">終了日</Label>
                              <DatePicker
                                id="end-date"
                                date={customEndDate}
                                onDateChange={(date) => {
                                  setCustomEndDate(date);
                                  setValidationError('');
                                }}
                                placeholder="終了日を選択"
                                aria-label="終了日"
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* バリデーションエラー - AnimatePresence */}
                    <AnimatePresence>
                      {validationError && (
                        <motion.div
                          variants={alertVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm flex items-start gap-2"
                          role="alert"
                          aria-live="polite"
                        >
                          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <span>{validationError}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* 成功メッセージ - AnimatePresence */}
                    <AnimatePresence>
                      {exportSuccess && (
                        <motion.div
                          variants={alertVariants}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm flex items-center gap-2"
                          role="status"
                          aria-live="polite"
                        >
                          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                          <span>CSVファイルのダウンロードが完了しました</span>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* ダウンロードボタン */}
                    <div className="flex justify-end gap-2">
                      <motion.div
                        variants={buttonHoverVariants}
                        initial="rest"
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <Button
                          variant="outline"
                          onClick={() => {
                            setExportDialogOpen(false);
                            setValidationError('');
                            setExportSuccess(false);
                          }}
                          disabled={isExporting}
                          aria-label="キャンセル"
                        >
                          キャンセル
                        </Button>
                      </motion.div>
                      <motion.div
                        variants={buttonHoverVariants}
                        initial="rest"
                        whileHover={isExporting ? "rest" : "hover"}
                        whileTap={isExporting ? "rest" : "tap"}
                      >
                        <Button
                          onClick={handleExportCSV}
                          className="gap-2"
                          disabled={isExporting}
                          aria-label={isExporting ? 'CSVファイル生成中...' : 'CSVファイルをダウンロード'}
                        >
                          {isExporting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              生成中...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4" />
                              ダウンロード
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </DialogContent>
            </Dialog>

            <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={Object.keys(selectedFilters).length > 0 ? 'default' : 'outline'}
                  size="icon"
                  aria-label="フィルターメニューを開く"
                  aria-expanded={filterOpen}
                  aria-haspopup="true"
                  title="フィルター"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-4 space-y-4" align="end">
                <div className="space-y-2">
                  <Label htmlFor="min-revenue">最小売上</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">¥</span>
                    <Input
                      id="min-revenue"
                      type="number"
                      placeholder="0"
                      value={selectedFilters.minRevenue || ''}
                      onChange={(e) =>
                        setSelectedFilters(prev => ({
                          ...prev,
                          minRevenue: e.target.value ? Number(e.target.value) : undefined,
                        }))
                      }
                      className="pl-7"
                      aria-label="最小売上フィルター"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min-customers">最小客数</Label>
                  <Input
                    id="min-customers"
                    type="number"
                    placeholder="0"
                    value={selectedFilters.minCustomers || ''}
                    onChange={(e) =>
                      setSelectedFilters(prev => ({
                        ...prev,
                        minCustomers: e.target.value ? Number(e.target.value) : undefined,
                      }))
                    }
                    aria-label="最小客数フィルター"
                  />
                </div>
                <div className="space-y-3 border-t pt-3">
                  <Label className="text-sm font-semibold">期間フィルター</Label>
                  <div className="space-y-2">
                    <Label htmlFor="filter-start-date" className="text-xs">開始日</Label>
                    <DatePicker
                      id="filter-start-date"
                      date={selectedFilters.startDate}
                      onDateChange={(date) =>
                        setSelectedFilters(prev => ({
                          ...prev,
                          startDate: date,
                        }))
                      }
                      placeholder="開始日を選択"
                      aria-label="フィルター開始日"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="filter-end-date" className="text-xs">終了日</Label>
                    <DatePicker
                      id="filter-end-date"
                      date={selectedFilters.endDate}
                      onDateChange={(date) =>
                        setSelectedFilters(prev => ({
                          ...prev,
                          endDate: date,
                        }))
                      }
                      placeholder="終了日を選択"
                      aria-label="フィルター終了日"
                    />
                  </div>
                </div>
                <div className="space-y-3 border-t pt-3">
                  <Label className="text-sm font-semibold">新規顧客率フィルター</Label>
                  <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="min-new-customer-rate" className="text-xs">最小 (%)</Label>
                      <Input
                        id="min-new-customer-rate"
                        type="number"
                        placeholder="0"
                        min="0"
                        max="100"
                        value={selectedFilters.minNewCustomerRate || ''}
                        onChange={(e) =>
                          setSelectedFilters(prev => ({
                            ...prev,
                            minNewCustomerRate: e.target.value ? Number(e.target.value) : undefined,
                          }))
                        }
                        aria-label="最小新規顧客率"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max-new-customer-rate" className="text-xs">最大 (%)</Label>
                      <Input
                        id="max-new-customer-rate"
                        type="number"
                        placeholder="100"
                        min="0"
                        max="100"
                        value={selectedFilters.maxNewCustomerRate || ''}
                        onChange={(e) =>
                          setSelectedFilters(prev => ({
                            ...prev,
                            maxNewCustomerRate: e.target.value ? Number(e.target.value) : undefined,
                          }))
                        }
                        aria-label="最大新規顧客率"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-3 border-t pt-3">
              <Label className="text-sm font-semibold">スタッフフィルター</Label>
              
              {/* Selected staff badges */}
              {selectedFilters.staffNames && selectedFilters.staffNames.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedFilters.staffNames.map((name) => (
                    <Badge key={name} variant="secondary" className="gap-1">
                      {name}
                      <button
                        onClick={() => {
                          setSelectedFilters(prev => ({
                            ...prev,
                            staffNames: prev.staffNames?.filter(n => n !== name),
                          }));
                        }}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full"
                        aria-label={`${name}を削除`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Command autocomplete */}
              <Popover open={staffCommandOpen} onOpenChange={setStaffCommandOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <Search className="mr-2 h-4 w-4" />
                    スタッフを追加
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="スタッフ名で検索..."
                      value={staffSearchInput}
                      onValueChange={setStaffSearchInput}
                    />
                    <CommandList>
                      <CommandEmpty>スタッフが見つかりません</CommandEmpty>
                      <CommandGroup>
                        {filteredStaffNames.map((name) => (
                          <CommandItem
                            key={name}
                            value={name}
                            onSelect={() => {
                              setSelectedFilters(prev => ({
                                ...prev,
                                staffNames: prev.staffNames 
                                  ? [...prev.staffNames, name]
                                  : [name],
                              }));
                              setStaffCommandOpen(false);
                              setStaffSearchInput('');
                            }}
                            disabled={selectedFilters.staffNames?.includes(name)}
                          >
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedFilters({});
                    setFilterOpen(false);
                  }}
                  className="w-full"
                  aria-label="フィルタをクリア"
                >
                  フィルタをクリア
                </Button>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="検索（日付、サービス、スタッフ名）"
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="pl-10"
            aria-label="売上データ検索"
          />
        </div>

        {/* Desktop: Table表示 (md以上) */}
        <div className="hidden md:block overflow-auto rounded-md border max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className={header.column.id === 'expand' ? 'w-12' : ''}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow className="hover:bg-muted/50">
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="p-0">
                          <Collapsible open={row.getIsExpanded()}>
                            <CollapsibleContent className="px-8 py-4 bg-muted/20">
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-sm font-bold mb-3">サービス別内訳</h4>
                                  <div className="overflow-auto rounded-md border">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>サービス</TableHead>
                                          <TableHead className="text-right">件数</TableHead>
                                          <TableHead className="text-right">売上</TableHead>
                                          <TableHead className="text-right">構成比</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {row.original.serviceDetails.map((service) => (
                                          <TableRow key={service.name}>
                                            <TableCell className="text-sm">{service.name}</TableCell>
                                            <TableCell className="text-sm text-right">{service.count}件</TableCell>
                                            <TableCell className="text-sm text-right">{formatCurrency(service.revenue)}</TableCell>
                                            <TableCell className="text-right">
                                              <div className="flex items-center justify-end gap-2">
                                                <Progress value={service.percentage} className="w-16 h-2" />
                                                <span className="text-xs text-muted-foreground min-w-[3rem]">
                                                  {service.percentage.toFixed(1)}%
                                                </span>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>

                                <div>
                                  <h4 className="text-sm font-bold mb-3">スタッフ別実績</h4>
                                  <div className="overflow-auto rounded-md border">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>スタッフ</TableHead>
                                          <TableHead className="text-right">対応客数</TableHead>
                                          <TableHead className="text-right">売上</TableHead>
                                          <TableHead className="text-right">稼働率</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {row.original.staffDetails.map((staff) => (
                                          <TableRow key={staff.name}>
                                            <TableCell className="text-sm">{staff.name}</TableCell>
                                            <TableCell className="text-sm text-right">{staff.customerCount}名</TableCell>
                                            <TableCell className="text-sm text-right">{formatCurrency(staff.revenue)}</TableCell>
                                            <TableCell className="text-right">
                                              <Badge variant={staff.efficiency >= 80 ? 'default' : 'secondary'}>
                                                {staff.efficiency.toFixed(0)}%
                                              </Badge>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    <span className="text-sm text-muted-foreground">データがありません</span>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: Card表示 (<md) */}
        <div className="md:hidden space-y-4">
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <Card key={row.id}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">
                        {new Date(row.original.date).toLocaleDateString('ja-JP', {
                          month: 'short',
                          day: 'numeric',
                          weekday: 'short',
                        })}
                      </CardTitle>
                      <p className="text-2xl font-bold mt-1">{formatCurrency(row.original.revenue)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => row.toggleExpanded()}
                      aria-label={row.getIsExpanded() ? '詳細を閉じる' : '詳細を開く'}
                    >
                      {row.getIsExpanded() ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">客数</p>
                      <p className="font-medium">{row.original.customers}名</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">平均単価</p>
                      <p className="font-medium">{formatCurrency(row.original.averageSpend)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">新規顧客数</p>
                      <p className="font-medium">{row.original.newCustomers}名</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">新規顧客率</p>
                      <p className="font-medium text-primary">{row.original.newCustomerRate}%</p>
                    </div>
                  </div>
                  
                  {/* Expanded content */}
                  <AnimatePresence>
                    {row.getIsExpanded() && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t space-y-4"
                      >
                        {/* サービス別内訳 */}
                        <div>
                          <h4 className="text-sm font-bold mb-2">サービス別内訳</h4>
                          <div className="space-y-2">
                            {row.original.serviceDetails.map((service) => (
                              <div key={service.name} className="flex justify-between items-center text-sm">
                                <span>{service.name}</span>
                                <span className="font-medium">{formatCurrency(service.revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* スタッフ別実績 */}
                        <div>
                          <h4 className="text-sm font-bold mb-2">スタッフ別実績</h4>
                          <div className="space-y-2">
                            {row.original.staffDetails.map((staff) => (
                              <div key={staff.name} className="flex justify-between items-center text-sm">
                                <span>{staff.name}</span>
                                <span className="font-medium">{formatCurrency(staff.revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center h-24">
                <span className="text-sm text-muted-foreground">データがありません</span>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">表示件数:</span>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[5, 10, 25, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <span className="text-sm text-muted-foreground">
            {table.getFilteredRowModel().rows.length > 0
              ? `${table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-${Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )} / ${table.getFilteredRowModel().rows.length}件`
              : '0件'}
          </span>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="前のページへ"
            >
              前へ
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="次のページへ"
            >
              次へ
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
